import { useState, useEffect } from 'react';
import { HowItWorks, Loading, api, formatMoney } from './_barrel.js';


        function SourceHealthView({ token }) {
            const [data, setData] = useState(null);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);

            useEffect(() => {
                (async () => {
                    try {
                        const r = await api('/api/v1/source-health', {}, token);
                        setData(r);
                    } catch (e) { setError(e.message); }
                })();
            }, [token, refresh]);

            if (error) return <div className="max-w-5xl mx-auto px-6 py-8 text-[var(--red)]">{error}</div>;
            if (!data) return <Loading />;

            const fmtAge = (iso) => {
                if (!iso) return 'never';
                const ageMs = Date.now() - new Date(iso).getTime();
                const mins = Math.floor(ageMs / 60000);
                if (mins < 60) return `${mins}m ago`;
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return `${hrs}h ago`;
                const days = Math.floor(hrs / 24);
                return `${days}d ago`;
            };

            const statusColour = s => s === 'fresh' ? 'var(--green)' :
                                      s === 'stale' ? 'var(--amber)' :
                                      s === 'never' ? 'var(--text-dim)' :
                                      'var(--muted)';

            const statusLabel = s => s === 'fresh' ? 'Fresh' :
                                     s === 'stale' ? 'Stale' :
                                     s === 'never' ? 'Never run' :
                                     'Unknown';

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex justify-between items-end mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Operations</div>
                            <h2 className="font-display text-5xl gold">Source Health</h2>
                            <div className="text-muted font-mono text-sm mt-2">
                                Every collector and integration · last run, recent activity, current state
                            </div>
                        </div>
                        <button className="btn-ghost" onClick={() => setRefresh(r => r+1)}>↻ Refresh</button>
                    </div>

                    <HowItWorks id="source-health" title="How Source Health works"
                        intro={'This is the engine room. Every intelligence feed the platform runs shows here, with when it last succeeded and whether it is fresh or stale — so you can prove coverage is live and catch a broken feed early.'}
                        steps={[
                            'Each row is one <b>collector</b> — social (Bluesky, Mastodon, X), official (Companies House, The Gazette), open-source (GDELT adverse-media, RSS/news), plus sanctions and breach.',
                            '<b>Fresh</b> means the feed ran inside its expected window (48h); <b>Stale</b> means it has not — investigate the <i>feed</i>, not the subject.',
                            'A stale feed does not hide a subject&rsquo;s risk — it means new signal from that one stream may be delayed.',
                            'The <b>24h</b> and <b>7d</b> columns show recent run counts; use them to spot a rate-limit or a dead API key.',
                            'The <b>Cost 30d</b> column shows the actual API spend for each paid source over the last 30 days (in GBP); keyless sources like GDELT, RSS and Companies House show <b>Free</b>.',
                        ]} />
                    <div className="panel">
                        <div className="px-6 py-3 border-b border-veil grid grid-cols-12 gap-4 text-xs font-mono uppercase tracking-widest text-muted">
                            <div className="col-span-4">Source</div>
                            <div className="col-span-1 text-center">Status</div>
                            <div className="col-span-2">Last Run</div>
                            <div className="col-span-2">Last Result</div>
                            <div className="col-span-1 text-right" title="Actual API spend for this source over the last 30 days">Cost 30d</div>
                            <div className="col-span-1 text-right">24h</div>
                            <div className="col-span-1 text-right">7d</div>
                        </div>
                        {data.collectors.map(c => (
                            <div key={c.action} className="table-row px-6 py-4 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-4">
                                    <div className="font-display text-base">{c.label}</div>
                                    <div className="text-xs text-muted font-mono">{c.description}</div>
                                </div>
                                <div className="col-span-1 text-center">
                                    <span className="font-mono text-xs uppercase tracking-widest"
                                          style={{color: statusColour(c.status)}}>
                                        {statusLabel(c.status)}
                                    </span>
                                </div>
                                <div className="col-span-2 text-sm">
                                    {c.last_run_at ? (
                                        <>
                                            <div className="font-mono text-xs">{fmtAge(c.last_run_at)}</div>
                                            {c.last_run_by && (
                                                <div className="text-xs text-muted font-mono">by {c.last_run_by}</div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-muted font-mono text-xs">—</span>
                                    )}
                                </div>
                                <div className="col-span-2 text-sm">
                                    {c.last_result_summary ? (
                                        <span className="font-mono text-xs">{c.last_result_summary}</span>
                                    ) : (
                                        <span className="text-muted font-mono text-xs">—</span>
                                    )}
                                </div>
                                <div className="col-span-1 text-right font-mono text-sm"
                                     title={c.paid ? 'Actual spend over the last 30 days' : 'Keyless / free source'}>
                                    {c.paid
                                        ? <span style={{color:'var(--gold)'}}>{formatMoney(c.cost_30d_usd || 0, 'GBP')}</span>
                                        : <span className="text-muted">Free</span>}
                                </div>
                                <div className="col-span-1 text-right font-mono text-sm">{c.runs_24h}</div>
                                <div className="col-span-1 text-right font-mono text-sm">{c.runs_7d}</div>
                            </div>
                        ))}
                    </div>

                    <div className="text-xs text-muted font-mono mt-6">
                        Generated at {new Date(data.generated_at).toLocaleString('en-GB')} · "Fresh" = ran within 48 hours · derived from audit log
                    </div>
                </div>
            );
        }

export { SourceHealthView };
